import { useTranslation } from "react-i18next";
import { IncentiveCalculation } from "../../../common/components/IncentiveCalculation/IncentiveCalculation";
import type {
  CsiBannerTile,
  CsiPerformanceBonusData,
} from "../../../services/CustomerSalesIncentive/CustomerSalesIncentive.types";
import { formatCurrencyAmount } from "../../../common/util/formatCurrency";
import defaultContent from "../../../content/csi";
import commonContent from "../../../content/common";
import "./BonusCalculation.scss";

type Props = {
  bannerTile: CsiBannerTile;
  perfBonus: CsiPerformanceBonusData;
  isLoading: boolean;
};

const BonusCalculation = ({ bannerTile, perfBonus, isLoading }: Props) => {
  const { t } = useTranslation(["csi", "common"]);
  const symbol = t("disclaimerSymbol", { ns: "common", defaultValue: commonContent.disclaimerSymbol });

  const bvFormatted = bannerTile.VerifiedCustomerBV.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const totalBonusAmount = formatCurrencyAmount(bannerTile.CSIBonus, bannerTile.CSIBonusCurrency);
  const perfBonusAmount = formatCurrencyAmount(
    perfBonus.PerfBonus,
    perfBonus.PerfBonusCurrency || bannerTile.CSIBonusCurrency,
  );

  return (
    <div className="BonusCalculation">
      <h2 className="BonusCalculation__title">{t("bonusCalculation.title", defaultContent.bonusCalculation.title)}</h2>

      <h3 className="BonusCalculation__section-title">
        {t("bonusCalculation.csiSectionTitle", defaultContent.bonusCalculation.csiSectionTitle, { symbol })}
      </h3>
      <IncentiveCalculation
        isLoading={isLoading}
        base={{
          title: t("bonusCalculation.verifiedBV", defaultContent.bonusCalculation.verifiedBV),
          amount: bvFormatted,
          hideDesktopIcon: true,
          hideMobileIcon: true,
        }}
        multiplier={{
          title: t("bonusCalculation.csiAtLevel", defaultContent.bonusCalculation.csiAtLevel, {
            level: bannerTile.CSILevel,
            symbol,
          }),
          amount: `${bannerTile.CSILevel}%`,
          hideDesktopIcon: true,
          hideMobileIcon: true,
        }}
        total={{
          title: t("bonusCalculation.totalPaid", defaultContent.bonusCalculation.totalPaid),
          amount: totalBonusAmount,
          hideDesktopIcon: true,
          hideMobileIcon: true,
        }}
      />

      {bannerTile.CSIBonusCapValue !== null && bannerTile.CSIBonusCapCurrency && (
        <div className="BonusCalculation__cap">
          {t("bannerTile.bonusCapped", defaultContent.bannerTile.bonusCapped, {
            cap: formatCurrencyAmount(bannerTile.CSIBonusCapValue, bannerTile.CSIBonusCapCurrency),
          })}
        </div>
      )}

      <h3 className="BonusCalculation__section-title">
        {t("bonusCalculation.perfBonusSectionTitle", defaultContent.bonusCalculation.perfBonusSectionTitle)}
      </h3>
      <IncentiveCalculation
        isLoading={isLoading}
        base={{
          title: t("bonusCalculation.verifiedBV", defaultContent.bonusCalculation.verifiedBV),
          amount: bvFormatted,
          hideDesktopIcon: true,
          hideMobileIcon: true,
        }}
        multiplier={{
          title: t("bonusCalculation.perfBonusAtLevel", defaultContent.bonusCalculation.perfBonusAtLevel, {
            level: perfBonus.PerfBonusLevel,
          }),
          amount: `${perfBonus.PerfBonusLevel}%`,
          hideDesktopIcon: true,
          hideMobileIcon: true,
        }}
        total={{
          title: t("bonusCalculation.totalPaid", defaultContent.bonusCalculation.totalPaid),
          amount: perfBonusAmount,
          hideDesktopIcon: true,
          hideMobileIcon: true,
        }}
      />

      {bannerTile.HasPPVRequirement && bannerTile.PPVExemptionApplied && (
        <div className="BonusCalculation__ppv-exempt">
          {t("bonusCalculation.ppvExempt", defaultContent.bonusCalculation.ppvExempt)}
        </div>
      )}
    </div>
  );
};

export default BonusCalculation;
